import React, { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { MainLayout } from '@/app/navigation'
import type { TabConfig } from '@/app/navigation'
import { expertTabs } from './config/tabs.config'
import { DashboardTab } from './tabs/dashboard'
import { ATraiterTab } from './tabs/a-traiter'
import { DossierEnAttenteTab } from './tabs/dossier-en-attente'
import { PlanningTab } from './tabs/planning'
import { ExpertiseDetailPage } from '@/ui/components/pages/expertise-detail'
import { AffaireDetailPage } from '@/features/admin/tabs/affaire-detail'
import { PreferencesTab } from './tabs/preferences'
import { SettingsTab } from './tabs/settings/SettingsTab'
import { TasksTab } from '@/features/admin/tabs/mes-taches'
import { IATab } from '@/features/admin/tabs/ia'
import { TrackerTab } from '@/features/admin/tabs/tracker'
import { useUnreadCount } from '@/features/admin/tabs/mes-taches/hooks/useUnreadCount'
import { UnreadProvider } from '@/features/admin/tabs/mes-taches/contexts/UnreadContext'

const ExpertiseDetailWrapper: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  // Get the back URL from location state, or default to a-traiter
  const backUrl = (location.state as any)?.from || '/expert/a-traiter'
  return <ExpertiseDetailPage dossierId={id || ''} backUrl={backUrl} />
}

// Inner component that has access to UnreadProvider context
const ExpertLayoutContent: React.FC<{ tabsWithBadge: TabConfig[] }> = ({ tabsWithBadge }) => {
  return (
    <MainLayout tabs={tabsWithBadge}>
      <Routes>
        {/* Tab routes */}
        <Route path="dashboard" element={<DashboardTab />} />
        <Route path="a-traiter" element={<ATraiterTab />} />
        <Route path="dossier-en-attente" element={<DossierEnAttenteTab />} />
        <Route path="planning" element={<PlanningTab />} />
        <Route path="mes-taches" element={<TasksTab />} />
        <Route path="ia" element={<IATab />} />
        <Route path="tracker" element={<TrackerTab />} />
        <Route path="preferences" element={<PreferencesTab />} />
        <Route path="settings" element={<SettingsTab />} />
        {/* Expertise detail route (legacy) */}
        <Route path="expertise/:id" element={<ExpertiseDetailWrapper />} />
        {/* Unified affaire detail page (with Fiche Bien tab for experts) */}
        <Route path="affaire/:affaireId" element={<AffaireDetailPage />} />

        {/* Default route - redirect to dashboard */}
        <Route path="/" element={<Navigate to="dashboard" replace />} />
        <Route index element={<Navigate to="dashboard" replace />} />
      </Routes>
    </MainLayout>
  )
}

export const ExpertLayout: React.FC = () => {
  const { unreadCount, refetch } = useUnreadCount();
  const [tabsWithBadge, setTabsWithBadge] = useState(expertTabs);

  useEffect(() => {
    // Update the mes-taches tab with unread count badge
    const updatedTabs = expertTabs.map(tab => {
      if (tab.id === 'mes-taches' && unreadCount > 0) {
        return {
          ...tab,
          badge: {
            value: unreadCount.toString(),
            variant: 'danger' as const
          }
        };
      }
      return tab;
    });
    setTabsWithBadge(updatedTabs);
  }, [unreadCount]);

  return (
    <UnreadProvider refetchUnreadCount={refetch}>
      <ExpertLayoutContent tabsWithBadge={tabsWithBadge} />
    </UnreadProvider>
  )
}